import React, { useEffect, useState } from 'react';
import ActionButton from 'components/ActionButton';
import styles from './index.module.scss';
import DataTable from 'components/DataTable';
import { DATE_FORMATS, formatDate, getPageLimit, showError, updatePageLimit } from 'utils';
import moment from 'moment';
import { tableStyles } from 'css/tableStyles';
import { getReportForInventoryItemSale } from 'services';
import { DateRangePickerWrapper } from 'components/Date/DateRangePicker';
import { Loading } from 'components/loading/Loading';
import CustomLink from 'components/TableLink';

let filters: {
  [p: string]: any;
  page: number;
  startDate: string | null;
  endDate: string | null;
} = {
  page: 0,
  limit: getPageLimit(),
  startDate: moment().subtract(1, 'week').toString(),
  endDate: moment().toString(),
};

const columns = [
  {
    name: 'Item ID',
    selector: 'objectId',
    sortable: false,
    width: '15%',
    // eslint-disable-next-line react/display-name
    cell: row => (
      <CustomLink
        text={row.objectId}
        linkTo={`/inventory-items/${row.objectId}`}
      />
    ),
  },
  {
    name: 'Name',
    selector: 'name',
    sortable: true,
    width: '35%',
  },
  {
    name: 'Category',
    selector: 'category.description',
    sortable: true,
  },
  {
    name: 'Sold Quantity',
    selector: 'soldQuantity',
    sortable: true,
    width: '20%',
  },
];

const InventoryItemSold = () => {
  const [data, setData] = useState<Record<string, any>[]>([]);
  const [startDate, setStartDate] = useState<string | null>(filters.startDate);
  const [endDate, setEndDate] = useState<string | null>(filters.endDate);
  const [loading, setLoading] = useState<boolean>(false);

  const fetchReport = async () => {
    setLoading(true);
    try {
      const response = await getReportForInventoryItemSale({
        ...filters,
        startDate: formatDate(filters.startDate, DATE_FORMATS.YYYY_MM_DD),
        endDate: formatDate(filters.endDate, DATE_FORMATS.YYYY_MM_DD),
      });
      setData(response);
    } catch (e) {
      showError(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchReport();
  }, []);

  const onChangePage = (page: number) => {
    filters.page = page - 1;
    fetchReport();
  };

  const onChangeRowsPerPage = (rowsPerPage: number, currentPage: number) => {
    filters.limit = rowsPerPage;
    updatePageLimit(rowsPerPage);
    onChangePage(currentPage);
  };

  const onDateSelectionChange = ({ startDate, endDate }: { startDate: Date; endDate: Date }) => {
    filters = {
      ...filters,
      startDate: moment(startDate).toISOString(),
      endDate: moment(endDate).toISOString(),
    };
    setStartDate(moment(startDate).toISOString());
    setEndDate(moment(endDate).toISOString());
  };

  const getDataTable = () => {
    if (loading) return <Loading type={'mini'} />;

    return (
      <DataTable
        keyField="objectId"
        columns={columns}
        noHeader={true}
        data={data}
        highlightOnHover={true}
        defaultSortField="createdAt"
        customStyles={tableStyles}
        pagination={true}
        paginationServer={true}
        paginationPerPage={getPageLimit()}
        paginationDefaultPage={(filters.page || 0) + 1}
        paginationTotalRows={data.length}
        paginationRowsPerPageOptions={[50, 100, 200]}
        onChangePage={onChangePage}
        onChangeRowsPerPage={onChangeRowsPerPage}
      />
    );
  };

  return (
    <>
      <div className={styles['header']}>
        <div className={styles['heading']}>Sold Inventory Item</div>
        <div className={styles['filter-container']}>
          <DateRangePickerWrapper
            startDate={startDate}
            endDate={endDate}
            onDatesChange={onDateSelectionChange}
          />
          <ActionButton
            text={'Submit'}
            active={!loading}
            isLoading={loading}
            onClick={fetchReport}
            customClass={'ml-4'}
          />
        </div>
      </div>
      {getDataTable()}
    </>
  );
};

export default InventoryItemSold;